import * as React from 'react';
import { connect } from 'react-redux';

import { PublicState } from 'rheostat';

import WorkflowStep from '../WorkflowStep';
import QuoteSlider from '../QuoteSlider';
import { Button } from '../../components/buttons';
import Utils from '../../common/utils';
import { IconTriangleRight, IconTriangleLeft } from '../../components/icons';

interface Props {
	currentUser: UserDetails;
	user: UserDetails;
	customerDetails: CustomerDemographicDetails;
	assessment: any;
	customerPolicy: CustomerPolicy;
	onCustomerPolicySelect: (plan: CustomerPolicy) => void;
	planOptions: CustomerPolicyDto[];
	actions: any;
	history: any;
	match: any;

	selectPolicyOptionsEnabled: boolean;
}
interface State {
	isLoading: boolean;
	rate: number;
}
class CustomSelectYourPlanPage extends React.Component<Props, State> {
	state = {
		isLoading: true,
		rate: 0
	};

	private COVERAGE_MAX = 1000000;
	private COVERAGE_MIN = 25000;
	private COVERAGE_DEFAULT = 500000;
	private TERM_MAX = 65 - (this.props.customerDetails.age || 0);
	private TERM_MIN = this.props.currentUser.isAuthenticated ? 5 : 10;
	private TERM_DEFAULT = 65 - (this.props.customerDetails.age || 0) >= 20 ?
	20 : (65 - (this.props.customerDetails.age || 0) <= 15 ? 10 : 15);

	componentDidMount() {
		this.setState({ isLoading: true });
		const { gender, id } = this.props.user;
		const { isSmoker, age } = this.props.customerDetails;

		if (age == null || age < 18 || age > 54 || gender == null || isSmoker == null) {
			this.props.history.replace(`/quote/${this.props.match.params.workflow}/about-you`);
		}
		else {
			const customerPolicy = { ...this.props.customerPolicy };
			if (this.props.customerPolicy.coverageAmount == null) {
				customerPolicy.coverageAmount = this.COVERAGE_DEFAULT;
				this.props.actions.setCustomerPolicy(customerPolicy);
			}
			if (this.props.customerPolicy.term == null || this.props.customerPolicy.term > this.TERM_DEFAULT) {
				customerPolicy.term = this.TERM_DEFAULT;
				this.props.actions.setCustomerPolicy(customerPolicy);
			}

			const { coverageAmount, term } = customerPolicy;
			const params: GetAdvisoryPoliciesRequest = {
				coverageAmount: coverageAmount,
				term: term,
				gender: gender,
				age,
				customerDemographicDetailsJson: this.props.customerDetails as string,
				childrenAge: this.props.assessment.childrenAge.map((a: number) => !a ? 0 : a).join(',') as number[],
				userId: id
			};
			this.props.actions.fetchAdvisoryPlanOptions(params)
				.then(() => this.setState({ isLoading: false }));
		}
	}

	onCoverageUpdate = (slider: PublicState) => {
		// Rheostat uses a values list to support multiple handles. Since we only have one handle, take the first index.
		const valuePct = slider.values[0] / 100;
		const customerPolicy = {
			...this.props.customerPolicy,
			coverageAmount: Math.round(this.COVERAGE_MAX * valuePct)
		};

		this.props.actions.setCustomerPolicy(customerPolicy);
	}
	onTermUpdate = (slider: PublicState) => {
		// Rheostat uses a values list to support multiple handles. Since we only have one handle, take the first index.
		const valuePct = slider.values[0] / 100;
		const customerPolicy = {
			...this.props.customerPolicy,
			term: Math.round(this.TERM_MAX * valuePct)
		};

		this.props.actions.setCustomerPolicy(customerPolicy);
	}
	onSliderDragEnd = () => {
		this.setState({ isLoading: true });
		const { coverageAmount, term } = this.props.customerPolicy;
		const { gender, id } = this.props.user;
		const { age } = this.props.customerDetails;

		const params: GetAdvisoryPoliciesRequest = {
			coverageAmount: coverageAmount,
			term: term,
			gender: gender,
			age,
			customerDemographicDetailsJson: this.props.customerDetails as string,
			childrenAge: this.props.assessment.childrenAge.map((a: number) => !a ? 0 : a).join(',') as number[],
			userId: id
		};
		this.props.actions.fetchAdvisoryPlanOptions(params)
			.then(() => this.setState({ isLoading: false }));
	}

	next = () => {
		const customerPolicy = {
			...this.props.customerPolicy,
			rate: this.props.planOptions[0].rate
		};

		this.props.onCustomerPolicySelect(customerPolicy);
	}

	render() {
		return (
			<WorkflowStep
				title="To see your quote, use the sliders to select the coverage amount and term that best meet your needs."
			>
				<div className="section section-lg section-bordered">
					<div className="row">
						<div className="col-lg-8">
							<QuoteSlider
								className="mb-4"
								min={this.COVERAGE_MIN}
								max={this.COVERAGE_MAX}
								value={this.props.customerPolicy.coverageAmount!}
								valuePrefix="$"
								titleSuffix="in coverage"
								onSliderDragEnd={this.onSliderDragEnd}
								onValuesUpdated={this.onCoverageUpdate}
							/>
							<QuoteSlider
								className="mb-4"
								min={this.TERM_MIN}
								max={this.TERM_MAX}
								value={this.props.customerPolicy.term!}
								titleSuffix="year term"
								onSliderDragEnd={this.onSliderDragEnd}
								onValuesUpdated={this.onTermUpdate}
								minSuffix=" years"
								maxSuffix=" years"
							/>
						</div>
						<div className="col-lg-4 quote-plans-advisory text-center">
							<div className="quote-plan">
								{this.props.planOptions.length > 0
									? (
										<div className="quote-plan-content">
											<div className="d-inline-flex align-items-center mb-3 px-1 bg-info-dark text-white">
												<IconTriangleRight className="svg-inline" />
												<div className="media-body mx-1">First two months FREE!</div>
												<IconTriangleLeft className="svg-inline" />
											</div>
											<h2 className="quote-plan-title text-secondary">
												{Utils.formatCurrency(this.props.planOptions[0].rate, 2)}/month
											</h2>
											<em className="text-secondary">may change based on lifestyle</em>
											<div className="quote-plan-actions">
												<Button
													onClick={this.next}
													className="btn-primary btn-block"
													disabled={!this.props.selectPolicyOptionsEnabled}
												>
													Get started
												</Button>
											</div>
										</div>
									)
									: (
										<div className="quote-plan-content">
											<h2 className="quote-plan-title text-secondary">
												Sorry, we currently do not have any plans available for you.
											</h2>
										</div>
									)
								}
							</div>
						</div>
					</div>
				</div>
			</WorkflowStep>
		);
	}
}

function mapStateToProps(state: any) {
	return {
		selectPolicyOptionsEnabled: state.settings.selectPolicyOptionsEnabled
	};
}

export default connect(mapStateToProps)(CustomSelectYourPlanPage);



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/quoting/custom/CustomSelectYourPlanPage.tsx